import React from 'react'
import { Container, Element, Icon, Level, Loader, Section, Tag } from 'react-bulma-components'
import Layout from '../../components/layout/layout'
import { useNavigate } from 'react-router-dom'
import useUrlId from '../../hooks/use-url-id'
import { useMissionQuery } from '../../queries/missions'
import MissionMessages from '../../components/missions/mission-messages'
import Breadcrumbs from '../../components/breadcrumbs/breadcrumbs'
import MissionPageTitle, { EditActions } from '../../components/missions/titles/mission-page-title'
import { dateToDateInputValue, endOfWeek, startOfWeek } from '../../utils/date'
import { IsoDate } from '../../hooks/use-calendar'
import { sortBy } from 'lodash'
import locales from '../../../../locales/missions'
import { getDocIdAsString } from '../../utils/documents'
import { useWeeksSummaryWorkPeriodsTable } from '../../hooks/use-weeks-summary-work-periods-table'
import { CalendarIcon, ClockIcon, InformationCircleIcon } from '@heroicons/react/outline'
import MissionWorkPeriodsPanel from '../../components/missions/mission-work-periods-panel'
import MissionHistoryPanel from '../../components/missions/mission-history-panel'
import MissionInfoPanel from '../../components/missions/mission-info-panel'

/** Which detail tab is shown (URL-agnostic; no extra browser history entries). */
type MissionDetailsTab = 'workPeriods' | 'history' | 'info'

const MISSION_DETAILS_TABS: {
  id: MissionDetailsTab
  label: string
  icon: React.ComponentType
}[] = [
  { id: 'workPeriods', label: 'Journées de travail', icon: CalendarIcon },
  { id: 'info', label: 'Informations', icon: InformationCircleIcon },
  { id: 'history', label: 'Historique', icon: ClockIcon },
]

const MissionDetails: React.FunctionComponent = () => {
  const navigate = useNavigate()
  const { id, isNew } = useUrlId()
  const { data: mission } = useMissionQuery(id)
  const isInterimAgencyMission = Boolean(mission !== undefined && 'interimAgency' in mission)

  const [activeTab, setActiveTab] = React.useState<MissionDetailsTab>('workPeriods')

  /**
   * Edit mode for the work periods inline calendar. Lifted here so that
   * both the week filter and `summaryFromTo` can relax their upper bound
   * (mission.end) while editing, allowing the user to add new weeks past
   * the current mission end (the backend extends `mission.end` automatically).
   */
  const [isEditingWorkPeriods, setIsEditingWorkPeriods] = React.useState(false)

  /**
   * Week filter: two date inputs (any day in the week); API uses Monday–Sunday for each bound.
   * Default is the **current calendar week**, intersected with the mission dates. Set when mission loads.
   */
  const [weekRangeFromIso, setWeekRangeFromIso] = React.useState<IsoDate | ''>('')
  const [weekRangeToIso, setWeekRangeToIso] = React.useState<IsoDate | ''>('')

  React.useEffect(() => {
    if (!mission) return
    const { from, to } = defaultMissionWeekFilter(mission)
    setWeekRangeFromIso(dateToDateInputValue(from))
    setWeekRangeToIso(dateToDateInputValue(to))
  }, [mission?._id])

  const summaryFromTo = React.useMemo(() => {
    if (!mission || !weekRangeFromIso || !weekRangeToIso) return null
    // When editing, do NOT clamp on mission.start / mission.end so users can
    // extend the calendar in either direction to add weeks before/after the
    // current mission window.
    const missionStart = !isEditingWorkPeriods ? startOfWeek(new Date(mission.start)) : null
    const missionEnd =
      !isEditingWorkPeriods && mission.end ? endOfWeek(new Date(mission.end)) : null
    let from = startOfWeek(new Date(weekRangeFromIso))
    let to = endOfWeek(new Date(weekRangeToIso))
    if (missionStart && from < missionStart) from = missionStart
    if (missionEnd && to > missionEnd) to = missionEnd
    if (from > to) {
      to = endOfWeek(from)
      if (missionEnd && to > missionEnd) to = missionEnd
    }
    return { from, to }
  }, [mission, weekRangeFromIso, weekRangeToIso, isEditingWorkPeriods])

  const weeksSummaryParams =
    mission && summaryFromTo && id
      ? {
          from: summaryFromTo.from,
          to: summaryFromTo.to,
          _id: getDocIdAsString(id),
        }
      : undefined

  const {
    workPeriods: workPeriodsForTable,
    employerRules: employerRulesForTable,
    resolveEmployerRuleDay,
    isLoading: weeksSummaryLoading,
  } = useWeeksSummaryWorkPeriodsTable(weeksSummaryParams, {
    enabled: Boolean(mission && id && summaryFromTo),
    missionId: id ? getDocIdAsString(id) : undefined,
  })

  const breadcrumbs = useBreadcrumbs(id)
  React.useEffect(() => {
    if (isNew) navigate('/')
  }, [isNew, navigate])

  const historyItems = React.useMemo(() => {
    return sortBy(mission?.history || [], 'createdAt').reverse()
  }, [mission])

  if (!mission) return <Loader />

  return (
    <Layout>
      <Section>
        <Container max={true} breakpoint="widescreen">
          <Level>
            <Level.Side>
              <Breadcrumbs items={breadcrumbs} />
            </Level.Side>
            <Level.Side align="right">{!isNew && <EditActions mission={mission} />}</Level.Side>
          </Level>
          <Level alignItems="flex-start" mb={4}>
            <Level.Side>
              <MissionPageTitle
                mission={mission}
                organizationType={isInterimAgencyMission ? 'interimAgency' : 'employer'}
              />
            </Level.Side>
            <Level.Side align="right">
              <Tag.Group>
                <MissionMessages mission={mission} type="tag" />
              </Tag.Group>
            </Level.Side>
          </Level>
          <nav className="tabs is-boxed mb-5" aria-label="Sections mission">
            <ul>
              {MISSION_DETAILS_TABS.map(tab => {
                const IconComponent = tab.icon
                const isActive = activeTab === tab.id
                return (
                  <li
                    key={tab.id}
                    className={isActive ? 'is-active' : undefined}
                    data-test={`mission-details-tab-${tab.id}`}
                  >
                    <a
                      role="tab"
                      aria-selected={isActive}
                      href="#"
                      onClick={event => {
                        event.preventDefault()
                        setActiveTab(tab.id)
                      }}
                    >
                      <Element className="is-flex is-align-items-center">
                        <Icon size="small" className="mr-2">
                          <IconComponent />
                        </Icon>
                        <span>{tab.label}</span>
                      </Element>
                    </a>
                  </li>
                )
              })}
            </ul>
          </nav>
          {activeTab === 'workPeriods' && (
            <MissionWorkPeriodsPanel
              mission={mission}
              weekRangeFromIso={weekRangeFromIso}
              weekRangeToIso={weekRangeToIso}
              onWeekRangeFromChange={setWeekRangeFromIso}
              onWeekRangeToChange={setWeekRangeToIso}
              summaryFromTo={summaryFromTo}
              weeksSummaryLoading={weeksSummaryLoading}
              workPeriodsForTable={workPeriodsForTable}
              employerRulesForTable={employerRulesForTable}
              resolveEmployerRuleDay={resolveEmployerRuleDay}
              isEditing={isEditingWorkPeriods}
              onEditingChange={setIsEditingWorkPeriods}
            />
          )}
          {activeTab === 'info' && <MissionInfoPanel mission={mission} />}
          {activeTab === 'history' && (
            <MissionHistoryPanel items={historyItems} locales={locales.history} />
          )}
        </Container>
      </Section>
    </Layout>
  )
}

export default MissionDetails

const useBreadcrumbs = (missionId?: string) =>
  React.useMemo(
    () => [
      {
        label: 'Liste des missions',
        to: '/missions',
      },
      {
        label: `Mission`,
        to: `/missions/${missionId}`,
      },
    ],
    [missionId],
  )

/**
 * Default week filter: **current calendar week** (Mon–Sun), intersected with the mission window.
 * If the mission does not overlap the current week (past-only mission), falls back to the last
 * mission week. For open-ended missions (CDI, no end date), the current week is always valid.
 */
function defaultMissionWeekFilter(mission: { start: Date | string; end?: Date | string | null }): {
  from: Date
  to: Date
} {
  const missionWeekStart = startOfWeek(new Date(mission.start))
  const missionWeekEnd = mission.end ? endOfWeek(new Date(mission.end)) : null
  const currentWeekStart = startOfWeek(new Date())
  const currentWeekEnd = endOfWeek(new Date())

  const overlapFrom = currentWeekStart > missionWeekStart ? currentWeekStart : missionWeekStart
  const overlapTo =
    missionWeekEnd && currentWeekEnd > missionWeekEnd ? missionWeekEnd : currentWeekEnd

  if (overlapFrom <= overlapTo) {
    return { from: overlapFrom, to: overlapTo }
  }

  if (new Date() < missionWeekStart) {
    const endFirstMissionWeek = endOfWeek(missionWeekStart)
    return {
      from: missionWeekStart,
      to:
        missionWeekEnd && endFirstMissionWeek > missionWeekEnd
          ? missionWeekEnd
          : endFirstMissionWeek,
    }
  }

  return {
    from: startOfWeek(missionWeekEnd!),
    to: missionWeekEnd!,
  }
}
